/**
 * Shared form field components — eliminates duplicate ownership and date range fields
 * across ApiForm, BackendForm, and ResourcePoolForm.
 */
import React from 'react';
import { Field, Input, Text, makeStyles, tokens } from '@fluentui/react-components';

// ── Ownership Section (Owner, Team, Contact) ──
const useStyles = makeStyles({
  ownershipGrid: {
    display: 'grid', gap: '8px', gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))',
    '@media (max-width: 600px)': { gridTemplateColumns: '1fr' },
  },
  dateGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px',
    '@media (max-width: 480px)': { gridTemplateColumns: '1fr' },
  },
});

export interface OwnershipData {
  owner: string;
  team: string;
  contact: string;
}

export function OwnershipSection({ data, onChange, label = 'Ownership' }: {
  data: OwnershipData;
  onChange: (data: OwnershipData) => void;
  label?: string;
}) {
  const styles = useStyles();
  return (
    <section style={{ display: 'grid', gap: 8, paddingTop: 4, borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
      <Text weight="semibold">{label}</Text>
      <div className={styles.ownershipGrid}>
        <Field label="Owner">
          <Input style={{ width: '100%' }} value={data.owner} onChange={(_, d) => onChange({ ...data, owner: d.value })} />
        </Field>
        <Field label="Team">
          <Input style={{ width: '100%' }} value={data.team} onChange={(_, d) => onChange({ ...data, team: d.value })} />
        </Field>
        <Field label="Contact">
          <Input style={{ width: '100%' }} value={data.contact} onChange={(_, d) => onChange({ ...data, contact: d.value })} />
        </Field>
      </div>
    </section>
  );
}

// ── Date Range Fields (Start Date, End Date) ──
export function DateRangeFields({ startDate, endDate, onStartChange, onEndChange, size = 'small' }: {
  startDate: string;
  endDate: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  size?: 'small' | 'medium' | 'large';
}) {
  const styles = useStyles();
  return (
    <div className={styles.dateGrid}>
      <Field label="Start Date"><Input size={size} type="date" value={startDate} onChange={(_, d) => onStartChange(d.value)} /></Field>
      <Field label="End Date"><Input size={size} type="date" value={endDate} onChange={(_, d) => onEndChange(d.value)} /></Field>
    </div>
  );
}
